import { useAnalytics } from '@ecosystem/newron-client-lib';
import { SortingOptionsType } from '@components/design/migrated/Table';
import { capitalize } from 'lodash';
import { useSelector } from 'react-redux';
import {
  RelativePeriod,
  RelativePeriodLabel,
  RelativePeriodMapping,
} from '@components/comparison-period-selector/Types';
import { ExportTrackingFormat } from '@hooks/export/export-tracking/ExportTracking';
import { ViewType } from '@hooks/export/Model';
import {
  getCountryFromSideFilters,
  getProductGroupFromSideFilters,
  segmentsFilterWithExcludeFilterKeys,
} from '@utils/FilterUtils';
import { SortDirectionMapping } from '@utils/hook/sorting/UsePersistedSorting';
import { FilterKeys, getPageTypeWithPageName, PageName, PageType } from '@utils/pageConfig/Types';
import {
  concatenateAllSelectedSegmentFeatureName,
  concatenateSelectedFeatureName,
  doAmplitudeAnalyticsTracking,
} from '../PageAnalytics';
import {
  AnalyticTrackingBasePropertiesObj,
  ElementType,
  TrackingGeneralProp,
} from './AnalyticTrackingBasePropertiesObj';
import { MainFiltersStateKeys, RetailerClientStateKeys } from '@state/reducers/Reducers';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { TradingAssistantTabTitle } from '@components/tab-nav/TabConfig';

// Example of tracking for - Data Exported
/**
 analytics.track("Data Export Initiated", {
 "access": "Retail Insights",
 "locationId": "Germany",
 "comparisonPeriodApplied": "Previous period", //if applicable
 "brandApplied": "Dyson", //if applicable
 "channelApplied": Total (Panel), //if applicable
 "periodicity": "Weekly", //if applicable
 "pageName"": "Performance Analysis",
 "itemGroup": "PTV",
 "elementType": "Table",
 "globalFiltersApplied":  {"XX, YY"}, //not yet
 "section": "Retail Intelligence",
 "subsection": "Performance Analysis",
 "elementKPI": "Unit Share",
 "elementSort" : "Absolute Ascending"
 })

 analytics.track("Data Exported", {
 "access": "Retail Insights",
 "locationId": "Germany",
 "optionChosen": "Excel" //ARRAY,
 "comparisonPeriodApplied": "Previous period", //if applicable
 "channelApplied": Total (Panel), //if applicable
 "brandApplied": "Dyson", //if applicable
 "periodicity": "Weekly", //if applicable
 "pageName"": "Performance Analysis",
 "itemGroup": "PTV",
 "elementType": "Table",
 "globalFiltersApplied":  {"XX, YY"}, //not yet
 "section": "Retail Intelligence",
 "subsection": "Performance Analysis",
 "elementKPI": "Unit Share",
 "elementSort" : "Absolute Ascending"
 })
 */

export const DATA_EXPORT_INITIATED_TRACKING_TITLE = 'Data Export Initiated';
export const DATA_EXPORTED_TRACKING_TITLE = 'Data Exported';

export interface DataExportTracking extends AnalyticTrackingBasePropertiesObj {
  elementType: string;
  elementKpi?: string;
  elementSort?: string;
  periodicity: string;
  focusPeriod: string;
  comparisonPeriod: RelativePeriodLabel;
  compareIn: string;
  globalFilterOptionsApplied?: string;
  optionChosen?: ExportTrackingFormat[];
  brandApplied?: string;
}

export interface DataExportTrackingParam {
  sideFilters: any;
  retailerClientState: any;
  track: (event: string, properties?: Object) => void;
  pageType: PageType;
  pageName: PageName;
  elementType: ElementType | ViewType;
  elementKpi?: string;
  elementSort?: SortingOptionsType;
  mainFilterState: any;
  optionChosen?: ExportTrackingFormat[];
  brandApplied?: string;
  access: Object;
}

export type DataExportTrackingElementKpiAndSortParam = Pick<DataExportTrackingParam, 'elementKpi' | 'elementSort'>;

const dataExportTrackingFunction = (eventTitle: string) => (param: DataExportTrackingParam) => {
  const {
    sideFilters,
    pageName,
    retailerClientState,
    pageType,
    track,
    elementType,
    elementKpi,
    elementSort,
    mainFilterState,
    optionChosen,
    access,
    brandApplied,
  } = param;

  const selectedSideFilter = sideFilters[pageType];

  const appliedProductGroup = getProductGroupFromSideFilters(selectedSideFilter);
  const appliedFiltersCountry = getCountryFromSideFilters(selectedSideFilter);

  const filteredSegments = segmentsFilterWithExcludeFilterKeys(selectedSideFilter, [
    FilterKeys.COUNTRY,
    FilterKeys.PRODUCT_GROUP,
    FilterKeys.CURRENCY,
  ]);

  const getPageName = pageName === PageName.TRADING_ASSISTANT ? TradingAssistantTabTitle.SupplierReview : pageName;

  const dataExportInitiatedProperties: DataExportTracking = {
    access: access,
    locationId: appliedFiltersCountry?.options.map((options) => options.label),
    itemGroup: concatenateSelectedFeatureName(appliedProductGroup),
    pageName,
    retailerDataFor: retailerClientState.clientName,
    section: TrackingGeneralProp.SECTION_TRACKING_PROP,
    subsection: getPageName,
    elementType,
    elementKpi,
    ...(elementSort && {
      elementSort: `${elementSort?.label} ${SortDirectionMapping[elementSort?.sortDir]}`,
    }),
    globalFilterOptionsApplied: concatenateAllSelectedSegmentFeatureName(filteredSegments),
    periodicity: mainFilterState.timeFrame,
    focusPeriod: mainFilterState.period?.label,
    comparisonPeriod: RelativePeriodMapping[mainFilterState.comparisonPeriod as RelativePeriod],
    compareIn: capitalize(mainFilterState.selectedGrowthType),
    optionChosen,
    brandApplied,
  };

  doAmplitudeAnalyticsTracking(eventTitle, track, dataExportInitiatedProperties);
};

type DataExportTrackingFunction = (param: DataExportTrackingParam) => void;

export const dataExportInitiatedTrackingFunction: DataExportTrackingFunction = (param: DataExportTrackingParam) =>
  dataExportTrackingFunction(DATA_EXPORT_INITIATED_TRACKING_TITLE)(param);

export const dataExportedTrackingFunction: DataExportTrackingFunction = (param: DataExportTrackingParam) =>
  dataExportTrackingFunction(DATA_EXPORTED_TRACKING_TITLE)(param);

export type DataExportTrackingElementParam = {
  elementType: ElementType;
  optionChosen?: ExportTrackingFormat[];
  elementKpi?: string;
  elementSort?: SortingOptionsType;
  brandApplied?: string;
};

type DataExportTrackingParamSubset = Omit<DataExportTrackingParam, keyof DataExportTrackingElementParam>;

export const useDataExportTracking: (
  mainFiltersStateKey: MainFiltersStateKeys,
  retailerClientStateKey?: RetailerClientStateKeys,
) => (elementParam: DataExportTrackingElementParam, dataExportTrackingFunc: DataExportTrackingFunction) => void = (
  mainFiltersStateKey,
  retailerClientStateKey = 'retailerClient',
) => {
  const { track } = useAnalytics();
  const pagesConfig = usePageConfig();
  const sideFiltersState = useSelector((state: any) => state.sideFilters);
  const mainFiltersState = useSelector((state: any) => state[mainFiltersStateKey]);
  const retailerClientState = useSelector((state: any) => state[retailerClientStateKey]);

  const dataExportParam: DataExportTrackingParamSubset = {
    sideFilters: sideFiltersState.appliedFilters,
    track,
    mainFilterState: mainFiltersState,
    retailerClientState,
    pageType: getPageTypeWithPageName(retailerClientState.pageViewing),
    pageName: retailerClientState.pageViewing,
    access: pagesConfig.access,
  };

  return (elementParam: DataExportTrackingElementParam, dataExportTrackingFunc: DataExportTrackingFunction) =>
    dataExportTrackingFunc({
      ...dataExportParam,
      ...elementParam,
    });
};
